// SortIcon Component
// This file defines a sort icon button for table headers, supporting ascending, descending, and default states.

import React from "react";
import { styled } from "@stitches/react";
import SortDefault from "@/components/Icons/sort-default";
import SortAsc from "@/components/Icons/sort-ascending";
import SortDesc from "@/components/Icons/sort-descending";
import { SortOrder } from "@/types/order-table";

/**
 * Wrapper
 * Styled button for the sort icon, with active state styling.
 */
const Wrapper = styled("button", {
  background: "none",
  border: "none",
  display: "flex",
  justifyContent: "center",

  "&:hover": {
    cursor: "pointer",
  },

  variants: {
    active: {
      true: {
        "& path": {
          stroke: "#0466B4",
        },
      },
    },
  },
});

/**
 * Props for the SortIcon component
 * @param sortOrder - Current sort order (ASC, DESC, or null)
 * @param onIconClick - Handler for icon click
 */
interface Props {
  sortOrder: SortOrder;
  onIconClick: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * Sort
 * Renders the appropriate sort icon based on the sort order.
 * @param sortOrder - Current sort order (ASC, DESC, or null)
 */
const Sort = ({ sortOrder }: { sortOrder: string | null }) => {
  if (sortOrder === null) {
    return <SortDefault />;
  }

  if (sortOrder === "ASC") {
    return <SortAsc />;
  } else {
    return <SortDesc />;
  }
};

/**
 * SortIcon Component
 * Renders a button with the appropriate sort icon and click handler.
 *
 * @param sortOrder - Current sort order
 * @param onIconClick - Handler for icon click
 */
const SortIcon = ({ sortOrder, onIconClick }: Props) => {
  return (
    <Wrapper onClick={onIconClick} active={sortOrder !== null}>
      <Sort sortOrder={sortOrder} />
    </Wrapper>
  );
};

export default SortIcon;
